import * as React from "react";
import { Link } from "gatsby";
import { useState, useEffect } from "react";
import { wrap } from "comlink";
import * as Sentry from "@sentry/browser";

/**
 * GeoRestriction component that blocks access for US users when blocked flag is true
 * Based on the design from defendonlineprivacy.com/ga/
 */
const GeoRestriction = ({ onRestrictionChange }) => {
  const [show, setShow] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Function to check localStorage for override settings
    const checkLocalStorageOverride = () => {
      if (typeof window === 'undefined') return null;
      
      try {
        const override = localStorage.getItem('geo-restriction-override');
        if (override === 'force-on') return true;
        if (override === 'force-off') return false;
        return null; // No override
      } catch (e) {
        console.error('Error accessing localStorage:', e);
        return null;
      }
    };
    
    // Function to fetch geo data from Cloudflare Worker
    const fetchGeoData = async () => {
      try {
        setLoading(true);
        
        // Check for localStorage override first
        const override = checkLocalStorageOverride();
        
        // If override is explicitly set, use that value
        if (override === true) {
          // Force show with mock data for testing
          setGeoData({
            country: localStorage.getItem('geo-restriction-country') || 'US',
            region: localStorage.getItem('geo-restriction-region') || 'a Red State',
            regionCode: localStorage.getItem('geo-restriction-regionCode') || 'RS',
            blocked: true,
            blocked_reason: 'Forced by localStorage override'
          });
          setShow(true);
          // Notify parent component about restriction state
          if (onRestrictionChange) onRestrictionChange(true);
          setLoading(false);
          return;
        } else if (override === false) {
          // Force hide
          setShow(false);
          // Notify parent component about restriction state
          if (onRestrictionChange) onRestrictionChange(false);
          setLoading(false);
          return;
        }
        
        // No override, proceed with normal geo detection
        // Initialize web worker
        const worker = new Worker("/workers/main.js");
        const obj = wrap(worker);
        
        // Add worker to tracking array for cleanup if it exists
        if (typeof window !== 'undefined' && window._activeFirebaseWorkers) {
          window._activeFirebaseWorkers.push(worker);
        }
        
        // Get geo data
        await obj.getGeo();
        // The worker stores the geo data in obj.data
        const data = obj.data;
        setGeoData(data);
        
        // Check if user is blocked (regardless of country)
        if (data && data.blocked === true) {
          setShow(true);
          // Store the blocked reason for display
          setGeoData(data);
          // Notify parent component about restriction state
          if (onRestrictionChange) onRestrictionChange(true);
        } else {
          // Notify parent component that user is not restricted
          if (onRestrictionChange) onRestrictionChange(false);
        }
        
        // Terminate worker when done
        worker.terminate();
        
        // Remove from tracking array
        if (typeof window !== 'undefined' && window._activeFirebaseWorkers) {
          const index = window._activeFirebaseWorkers.indexOf(worker);
          if (index > -1) {
            window._activeFirebaseWorkers.splice(index, 1);
          }
        }
      } catch (error) {
        // Report error to Sentry
        Sentry.withScope((scope) => {
          scope.setTag('component', 'GeoRestriction');
          scope.setTag('function', 'fetchGeoData');
          scope.setLevel('error');
          Sentry.captureException(error);
        });
        console.error("Error fetching geo data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch geo data when component mounts
    fetchGeoData();

    // Cleanup function
    return () => {
      // Any cleanup needed
    };
  }, [onRestrictionChange]);

  // If not showing restriction or still loading, return null
  if (!show || loading) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900 text-white p-4 overflow-auto">
      <div className="w-full max-w-3xl mx-auto mb-8 flex justify-center space-x-4">
        <a href="https://defendonlineprivacy.com/action" target="_blank" rel="noopener noreferrer" className="text-white hover:text-red-300 font-semibold">Take Action!</a>
        <a href="https://defendonlineprivacy.com/learn" target="_blank" rel="noopener noreferrer" className="text-white hover:text-red-300 font-semibold">Learn More</a>
        <a href="https://defendonlineprivacy.com/tools" target="_blank" rel="noopener noreferrer" className="text-white hover:text-red-300 font-semibold">Tools for Websites</a>
      </div>
      <div className="max-w-3xl mx-auto text-center p-6 rounded-lg border-l-8 border-red-600 bg-gray-800 shadow-2xl">
        <h1 className="text-4xl font-bold mb-6">We're Sorry.</h1>
        
        <h2 className="text-2xl font-semibold mb-4">
          We've had to block access from {geoData.region || geoData.country}.
        </h2>
        
        <div className="text-lg mb-8 text-left">
          <p className="mb-4">
            Due to recent legislation in {geoData.region || geoData.country}, we are unable to provide access to our content.
          </p>
          <p className="mb-4">
            We don't feel that there are adequate protections in place for your privacy. And because we can be sued if we don't verify, we've chosen to block the state entirely.
          </p>
          <p className="mb-4 font-bold">
            Mad? You should be. There are much better ways to protect children online than forcing you to show your government ID.*
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row justify-center gap-4 mb-10">
          {geoData.country !== "UK" ? (
            <a 
              href={`https://www.defendonlineprivacy.com/${geoData.regionCode.toLowerCase()}/action.php`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-red-700 hover:bg-red-600 rounded-lg font-bold transition-colors duration-200 shadow-lg"
            >
              Contact Your Legislators
            </a>
          ) : (
            <a 
              href="https://members.parliament.uk/FindYourMP"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-red-700 hover:bg-red-600 rounded-lg font-bold transition-colors duration-200 shadow-lg"
            >
              Contact Your MP
            </a>
          )}
        </div>
        
        <div className="text-sm text-gray-400 max-w-2xl mx-auto text-left mt-8 border-t border-gray-700 pt-4">
          <p className="mb-4">
            *Don't get us wrong. We don't want minors on our site either. But it makes far more sense to us to encourage the use of parental controls than to risk the disclosure of your sensitive data. If you want to protect yourself or your kids from accessing adult content, there are great easy-to-use features on <a 
            href="https://support.apple.com/guide/deployment-education/take-advantage-of-content-filtering-edu85ae8dd53/web" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline"
          >Apple</a> and <a 
            href="https://play.google.com/store/apps/details?id=com.google.android.apps.kids.familylink" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline"
          >Android</a> that are free. Spread the word.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GeoRestriction;
